import React from "react"
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  useMediaQuery
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import DescriptionIcon from "@material-ui/icons/Description"
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf"
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile"
import ImageIcon from "@material-ui/icons/Image"
import GetAppIcon from "@material-ui/icons/GetApp"
import useStore from "src/v2/utils/store"
import { useAlertStore } from "src/v2/utils/alert-store"

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(4),
  },
  docPaper: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: theme.shadows[4],
      background: "#f5faff",
      "& $downloadIcon": {
        opacity: 1,
      }
    },
    marginBottom: theme.spacing(2),
  },
  icon: {
    marginRight: theme.spacing(2),
    fontSize: 32,
    color: "#1976d2",
  },
  docName: {
    flex: 1,
    fontWeight: 500,
    fontSize: 16,
    wordBreak: "break-all",
  },
  downloadIcon: {
    opacity: 0,
    transition: "opacity 0.2s ease",
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
  },
  downloadIconVisible: {
    opacity: 1,
  }
}))

// Helper to get icon based on file extension
function getFileIcon(filename) {
  if (!filename) return <InsertDriveFileIcon />
  const ext = filename.split(".").pop().toLowerCase()
  if (["pdf"].includes(ext))
    return <PictureAsPdfIcon style={{ color: "#d32f2f" }} />
  if (["png", "jpg", "jpeg", "gif"].includes(ext))
    return <ImageIcon style={{ color: "#388e3c" }} />
  if (["doc", "docx"].includes(ext))
    return <DescriptionIcon style={{ color: "#1976d2" }} />
  if (["xls", "xlsx"].includes(ext))
    return <InsertDriveFileIcon style={{ color: "#388e3c" }} />
  if (["txt", "md"].includes(ext))
    return <InsertDriveFileIcon style={{ color: "#616161" }} />
  return <InsertDriveFileIcon />
}

// Helper to get file name from metadata or id
function getFileName(doc) {
  // Show originalName if present, else fallback to name, filename, id
  return doc?.originalName || doc?.name || doc?.filename || doc?._id || doc?.id || doc || "Document"
}

// Helper to get display name for architecture/specification docs
function getDisplayName(doc, type = "") {
  // Show the original file name as the label
  return getFileName(doc)
}

export default function Documents() {
  const classes = useStyles()
  const theme = useTheme()
  const oneReview = useStore((s) => s.oneReview)
  const downloadDocumentFromStore = useStore((s) => s.downloadDocument)
  const showAlert = useAlertStore((s) => s.showAlert)
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('sm'))

  // These can be array of ids or array of objects with metadata
  const architectureDocs = oneReview?.architectureDocuments || []
  const specificationDocs = oneReview?.specificationsDocuments || []

  // Function that uses the store's downloadDocument function
  const handleDownload = async (doc, type) => {
    const docId = doc?.id || doc?._id || doc
    const filename = getFileName(doc)
    
    if (!docId) {
      showAlert({
        message: "Invalid document ID",
        severity: "error",
        title: "Download Error",
      })
      return
    }
    
    await downloadDocumentFromStore(docId, filename, showAlert)
  }

  // Render a document item with download icon
  const renderDoc = (doc, idx, type = "") => (
    <Paper
      key={doc?.id || doc?._id || doc || idx}
      className={classes.docPaper}
      elevation={2}
      onClick={() => handleDownload(doc, type)}
    >
      {getFileIcon(getFileName(doc))}
      <span className={classes.docName}>{getDisplayName(doc, type)}</span>
      <Tooltip title="Download">
        <IconButton
          size="small"
          className={`${classes.downloadIcon} ${isMobileOrTablet ? classes.downloadIconVisible : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            handleDownload(doc, type)
          }}
        >
          <GetAppIcon />
        </IconButton>
      </Tooltip>
    </Paper>
  )

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Requirments Documents
          </Typography>
          {specificationDocs.length === 0 ? (
            <Typography color="textSecondary">
              No Requirments documents uploaded.
            </Typography>
          ) : (
            specificationDocs.map((doc, idx) => renderDoc(doc, idx, "specification"))
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Architecture Documents
          </Typography>
          {architectureDocs.length === 0 ? (
            <Typography color="textSecondary">
              No architecture documents uploaded.
            </Typography>
          ) : (
            architectureDocs.map((doc, idx) => renderDoc(doc, idx, "architecture"))
          )}
        </Grid>
      </Grid>
    </Box>
  )
}
